import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { CheckCircle2Icon, PencilIcon, PlusIcon } from 'lucide-react';

import { Button } from 'components/Button';
import { Checkbox } from 'components/Checkbox';
import { ToastAction } from 'components/Toast';
import { Flex } from 'components/layout';
import ButtonLoader from 'components/loading/ButtonLoader';
import { Typography } from 'components/typography';
import {
  DrumkitPlatform,
  SidebarStateContext,
  supportsDraftResponse,
} from 'contexts/sidebarStateContext';
import { useServiceFeatures } from 'hooks/useServiceContext';
import { useToast } from 'hooks/useToaster';
import { getCustomerLTLAccessorials } from 'lib/api/getCustomerLTLAccessorials';
import {
  QuoteTerminatingAction,
  updateQuoteRequestSuggestion,
} from 'lib/api/updateQuoteRequestSuggestion';
import {
  DraftReplyMode,
  createMailClientInstance,
} from 'lib/mailclient/interface';
import { BatchLTLReplyAction, BatchLTLReplySummary } from 'types/BatchLTLReply';
import { Email } from 'types/Email';
import { Maybe, Undef } from 'types/UtilityTypes';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';
import { SuggestionPipelines } from 'types/suggestions/CoreSuggestions';
import { SuggestionStatus } from 'types/suggestions/LoadSuggestions';
import { SuggestedQuoteChange } from 'types/suggestions/QuoteSuggestions';
import captureException from 'utils/captureException';
import { applyCompleteKitSuggestions } from 'utils/completeKitSuggestion';

import { useCubicFeetHeightConfig } from '../LTL/hooks/useCubicFeetHeightConfig';
import { useQuickQuoteContext } from '../context/QuickQuoteContext';
import { useDraftRecipients } from '../hooks/useDraftRecipients';
import { LTLQuote, LTLSelectedAccessorial, QuickQuoteInputs } from '../types';
import { getLTLQuoteIdentity } from '../util';
import { normalizeStopsFromLocationLine } from '../utils/parseStopAddressFromLocation';
import { BatchLTLBulkEdit } from './BatchLTLBulkEdit';
import { BatchLTLCustomerSection } from './BatchLTLCustomerSection';
import { BatchLTLDraftSection } from './BatchLTLDraftSection';
import { BatchLTLShipmentCard } from './BatchLTLShipmentCard';
import {
  BatchLTLShipmentForm,
  BatchLTLShipmentFormMethods,
} from './BatchLTLShipmentForm';
import { BatchLTLSummaryBar } from './BatchLTLSummaryBar';
import {
  BATCH_LTL_REMOVE_UNDO_MS,
  BATCH_LTL_STORAGE_SCHEMA_VERSION,
} from './constants';
import { useBatchLTLDraft } from './hooks/useBatchLTLDraft';
import { useBatchLTLRating } from './hooks/useBatchLTLRating';
import { useBatchLTLTemplate } from './hooks/useBatchLTLTemplate';
import { usePersistedBatchLTLState } from './hooks/usePersistedBatchLTLState';
import {
  BatchLTLBulkEditValues,
  BatchLTLPersistedRun,
  BatchLTLShipmentInput,
  BatchLTLShipmentRun,
  BatchLTLShipmentStatus,
} from './types';
import { applyBatchLTLBulkEdit } from './utils/applyBatchLTLBulkEdit';
import {
  buildBatchLTLStorageKey,
  loadBatchLTLState,
  toPersistedRuns,
} from './utils/batchLTLStorage';
import {
  getBatchLTLStatusCounts,
  getReplyConfirmationText,
  pluralize,
} from './utils/batchLTLSummary';
import {
  buildBatchLTLDefaultValues,
  buildEmptyBatchLTLShipment,
} from './utils/buildBatchLTLDefaultValues';
import { buildBatchLTLShipmentRequest } from './utils/buildBatchLTLShipmentRequest';
import { BatchLTLDraftShipment } from './utils/formatBatchLTLDraftTemplate';
import {
  getEditedFieldPaths,
  isAddedShipmentKey,
  restoreBatchLTLShipments,
} from './utils/restoreBatchLTLState';
import { getSharedOriginLabel } from './utils/shipmentDisplay';
import { submitBatchLTLFinalQuotes } from './utils/submitBatchLTLFinalQuotes';
import { validateBatchLTLShipment } from './utils/validateBatchLTLShipment';

// Mail clients that can put the batch reply into the thread as a draft.
const REPLY_DRAFT_PLATFORMS = [DrumkitPlatform.Outlook, DrumkitPlatform.Front];

type BatchLTLQuoteFormProps = {
  suggestions: SuggestedQuoteChange[];
  email: Maybe<Email>;
  // Hands one shipment to the single Quick Quote form.
  onOpenInQuickQuote?: (quoteRequestId: number) => void;
  // Remounts the panel with fresh defaults after the saved work is cleared.
  onStartOver?: () => void;
};

// Rates every LTL shipment in the email with the existing LTL job and builds one reply.
export function BatchLTLQuoteForm({
  suggestions,
  email,
  onOpenInQuickQuote,
  onStartOver,
}: BatchLTLQuoteFormProps) {
  const { toast } = useToast();
  const { isSharedMailboxUser, serviceID, quickQuoteConfig } =
    useServiceFeatures();
  const {
    currentState: {
      drumkitPlatform,
      threadItemId,
      isOutlookReply,
      isSharedMailboxEmail,
      batchLTLReplySummary,
    },
    setCurrentState,
  } = useContext(SidebarStateContext);
  const { ltlCustomer, ltlCustomerStaff, ltlAccessorials, setLtlAccessorials } =
    useQuickQuoteContext();
  const cubicFeetCalculationConfig = useCubicFeetHeightConfig();
  const recipients = useDraftRecipients();
  const batchEmailId = suggestions[0]?.emailID ?? email?.id ?? 0;
  const threadId = email?.threadID ?? '';
  const storageKey = threadId
    ? buildBatchLTLStorageKey(serviceID, threadId, batchEmailId)
    : null;

  const templateBody = useBatchLTLTemplate();
  // Inputs outside the shipment cards (the draft's recipient fields) read a form context;
  // each card has its own form.
  const panelFormMethods = useForm<QuickQuoteInputs>();

  // Built once per mount from the newest suggestions plus the rep's saved edits; the tab
  // remounts the form when the email's shipments change, so new data never overwrites
  // what the rep typed.
  const [restored] = useState(() => {
    const saved = storageKey
      ? loadBatchLTLState(storageKey, batchEmailId)
      : null;
    const defaults = buildBatchLTLDefaultValues(
      suggestions,
      undefined,
      quickQuoteConfig
    );
    const { shipments } = restoreBatchLTLShipments(defaults, saved);
    const shipmentKeys = shipments.map(({ key }) => key);

    return {
      saved,
      defaults,
      shipments,
      runs: pickByKey(saved?.runs ?? {}, shipmentKeys),
      quoteSelectionOverrides: pickByKey(
        saved?.quoteSelectionOverrides ?? {},
        shipmentKeys
      ),
    };
  });
  const { saved } = restored;
  const savedReplySummary = saved?.replySummary ?? null;
  const activeReplySummary =
    batchLTLReplySummary?.emailId === batchEmailId
      ? batchLTLReplySummary
      : savedReplySummary;

  // Every shipment's values (each card owns a single LTL form), in the shipper's order.
  const [allShipments, setAllShipments] = useState<BatchLTLShipmentInput[]>(
    restored.shipments
  );
  // Each card's form, so bulk edit and Shiplify can write into it.
  const formRegistryRef = useRef(
    new Map<number, BatchLTLShipmentFormMethods>()
  );
  const [removedKeys, setRemovedKeys] = useState<number[]>(
    () => saved?.removedQrIds ?? []
  );
  const [excludedKeys, setExcludedKeys] = useState<number[]>(
    () => saved?.excludedFromReplyQrIds ?? []
  );
  const shipments = useMemo(
    () => allShipments.filter(({ key }) => !removedKeys.includes(key)),
    [allShipments, removedKeys]
  );

  const handleRegisterForm = useCallback(
    (shipmentKey: number, formMethods: Maybe<BatchLTLShipmentFormMethods>) => {
      if (formMethods) {
        formRegistryRef.current.set(shipmentKey, formMethods);
      } else {
        formRegistryRef.current.delete(shipmentKey);
      }
    },
    []
  );

  const invalidateShipmentRef = useRef<(key: number) => void>(() => undefined);
  const programmaticShipmentUpdatesRef = useRef(new Set<number>());
  const handleValuesChange = useCallback(
    (shipmentKey: number, values: QuickQuoteInputs) => {
      const isProgrammaticUpdate =
        programmaticShipmentUpdatesRef.current.has(shipmentKey);
      if (!isProgrammaticUpdate) {
        invalidateShipmentRef.current(shipmentKey);
        setQuoteSelectionOverrides((previous) =>
          omitKeys(previous, [shipmentKey])
        );
        setRepliedKeys((previous) =>
          previous.filter((key) => key !== shipmentKey)
        );
      }
      setAllShipments((prev) =>
        prev.map((shipment) =>
          shipment.key === shipmentKey
            ? withShipmentMeta(values, shipment)
            : shipment
        )
      );
    },
    []
  );

  // Shiplify's lookups and recommended accessorials go back into the card's form, as the
  // single form does, so the rep sees what was sent.
  const handleShiplifyChecked = useCallback(
    (shipmentKey: number, values: QuickQuoteInputs) => {
      const formMethods = formRegistryRef.current.get(shipmentKey);
      programmaticShipmentUpdatesRef.current.add(shipmentKey);
      setTimeout(
        () => programmaticShipmentUpdatesRef.current.delete(shipmentKey),
        0
      );
      formMethods?.setValue(
        'ltl.selectedAccessorials',
        values.ltl?.selectedAccessorials ?? [],
        { shouldDirty: true }
      );
      formMethods?.setValue(
        'ltl.shiplifyLookups',
        values.ltl?.shiplifyLookups ?? {},
        { shouldDirty: true }
      );
    },
    []
  );

  // An added shipment gets its quote request when it is first rated; later rates and the
  // Accepted update use it.
  const handleQuoteRequestResolved = useCallback(
    (shipmentKey: number, quoteRequestId: number) =>
      setAllShipments((prev) =>
        prev.map((shipment) =>
          shipment.key === shipmentKey &&
          quoteRequestId > 0 &&
          shipment.quoteRequestId !== quoteRequestId
            ? { ...shipment, quoteRequestId }
            : shipment
        )
      ),
    []
  );

  const buildRequest = useCallback(
    (shipmentKey: number, formValues: QuickQuoteInputs) => {
      const shipment = allShipments.find(({ key }) => key === shipmentKey);

      return buildBatchLTLShipmentRequest(
        withShipmentMeta(formValues, shipment),
        {
          ltlCustomer,
          ltlCustomerStaff,
          emailId: batchEmailId,
          threadId: email?.threadID ?? '',
          cubicFeetCalculationConfig,
        }
      );
    },
    [
      allShipments,
      ltlCustomer,
      ltlCustomerStaff,
      batchEmailId,
      email?.threadID,
      cubicFeetCalculationConfig,
    ]
  );

  const { runsByKey, rateShipments, invalidateShipment, isRating } =
    useBatchLTLRating({
      savedRuns: restored.runs,
      buildRequest,
      onShiplifyChecked: handleShiplifyChecked,
      onQuoteRequestResolved: handleQuoteRequestResolved,
    });
  invalidateShipmentRef.current = invalidateShipment;

  const [selectedKeys, setSelectedKeys] = useState<number[]>([]);
  // Cards the rep needs to fix start open; after that only the rep opens or closes a
  // card, so fixing the last missing field never collapses the input being typed in.
  const [expandedKeys, setExpandedKeys] = useState<number[]>(
    () =>
      saved?.expandedQrIds ??
      restored.shipments
        .filter((shipment) => !validateBatchLTLShipment(shipment).isValid)
        .map((shipment) => shipment.key)
  );
  const [quoteSelectionOverrides, setQuoteSelectionOverrides] = useState<
    Record<number, string[]>
  >(restored.quoteSelectionOverrides);
  const [isBulkEditOpen, setIsBulkEditOpen] = useState(false);
  const [isSendingReply, setIsSendingReply] = useState(false);
  // Shipments the rep's reply covered; re-rating one takes it back out.
  const [repliedKeys, setRepliedKeys] = useState<number[]>(
    () => saved?.repliedQrIds ?? []
  );
  const [acceptedQrIds, setAcceptedQrIds] = useState<number[]>(
    () => saved?.acceptedQrIds ?? []
  );
  // Draft section, for the summary bar's "Reply" jump.
  const draftSectionRef = useRef<HTMLDivElement>(null);
  // Removed shipments whose quote request is rejected once the Undo window passes.
  const pendingRejectsRef = useRef(
    new Map<number, { timer: ReturnType<typeof setTimeout>; qrId: number }>()
  );

  // Tai's accessorial catalog is unscoped; load it once like the single LTL form.
  useEffect(() => {
    if (ltlAccessorials.length > 0) {
      return;
    }

    let cancelled = false;
    getCustomerLTLAccessorials().then((result) => {
      if (!cancelled && result.isOk()) {
        setLtlAccessorials(result.value);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [ltlAccessorials.length, setLtlAccessorials]);

  // Leaving the email before Undo runs out still rejects the removed quote requests.
  useEffect(() => {
    const pendingRejects = pendingRejectsRef.current;
    return () => {
      pendingRejects.forEach(({ timer, qrId }) => {
        clearTimeout(timer);
        void rejectQuoteRequest(qrId);
      });
      pendingRejects.clear();
    };
  }, []);

  const rows = useMemo(
    () =>
      shipments.map((shipment, index) => {
        const validation = validateBatchLTLShipment(shipment);
        const run = runsByKey[shipment.key];
        const selectedQuoteIds = getSelectedQuoteIds(
          run,
          quoteSelectionOverrides[shipment.key]
        );

        return {
          shipment,
          index,
          validation,
          run,
          status: getShipmentStatus(
            validation.isValid,
            run,
            repliedKeys.includes(shipment.key)
          ),
          selectedQuoteIds,
          isIncludedInReply: !excludedKeys.includes(shipment.key),
        };
      }),
    [shipments, runsByKey, quoteSelectionOverrides, repliedKeys, excludedKeys]
  );

  const readyRows = rows.filter(
    ({ status, validation }) =>
      validation.isValid &&
      status !== BatchLTLShipmentStatus.Rating &&
      status !== BatchLTLShipmentStatus.Rated &&
      status !== BatchLTLShipmentStatus.InReply
  );
  const hasRuns = rows.some(({ run }) => Boolean(run));
  const statusCounts = getBatchLTLStatusCounts(
    rows.map(({ status }) => status)
  );
  const sharedOriginLabel = getSharedOriginLabel(shipments);
  const skippedCount = shipments.length - readyRows.length;

  // The reply leaves out shipments the rep excluded and rated shipments with no carrier
  // checked; the Accepted update follows the same list.
  const draftShipments = useMemo<(BatchLTLDraftShipment & { key: number })[]>(
    () =>
      rows
        .map(
          ({
            shipment,
            run,
            validation,
            selectedQuoteIds,
            isIncludedInReply,
          }) => ({
            key: shipment.key,
            shipment,
            run,
            isIncludedInReply,
            missingFields: validation.missingFields,
            selectedQuotes: (run?.quotes ?? []).filter((quote) =>
              selectedQuoteIds.includes(getLTLQuoteIdentity(quote))
            ),
          })
        )
        .filter(
          ({ run, isIncludedInReply, selectedQuotes }) =>
            isIncludedInReply &&
            !(
              run?.status === BatchLTLShipmentStatus.Rated &&
              selectedQuotes.length === 0
            )
        ),
    [rows]
  );

  const { draft, editedDraft, isRepEdited, saveRepEdit, resetToGenerated } =
    useBatchLTLDraft({
      templateBody,
      initialEditedDraft: saved?.editedDraft ?? null,
      customerName: ltlCustomer?.customerName ?? null,
      shipments: draftShipments,
      hideTaiLimitedLiability:
        quickQuoteConfig?.hideTaiLimitedLiability === true,
    });

  const editedFieldsKey = JSON.stringify(
    getEditedFieldPaths(restored.defaults, allShipments)
  );
  const persistedRuns = useMemo(() => toPersistedRuns(runsByKey), [runsByKey]);
  const hasAddedShipments = allShipments.some(({ key }) =>
    isAddedShipmentKey(key)
  );
  const hasRepWork =
    editedFieldsKey !== '{}' ||
    hasAddedShipments ||
    removedKeys.length > 0 ||
    excludedKeys.length > 0 ||
    Object.keys(persistedRuns).length > 0 ||
    Object.keys(quoteSelectionOverrides).length > 0 ||
    acceptedQrIds.length > 0 ||
    repliedKeys.length > 0 ||
    editedDraft !== null;
  const stateToSave = useMemo(
    () =>
      hasRepWork
        ? {
            schemaVersion: BATCH_LTL_STORAGE_SCHEMA_VERSION,
            emailId: batchEmailId,
            suggestionIds: suggestions.map(({ id }) => id),
            suggestions,
            shipments: allShipments,
            editedFields: JSON.parse(editedFieldsKey) as Record<
              number,
              string[]
            >,
            ltlCustomer,
            ltlCustomerStaff,
            runs: persistedRuns,
            quoteSelectionOverrides,
            expandedQrIds: expandedKeys,
            acceptedQrIds,
            repliedQrIds: repliedKeys,
            editedDraft,
            replySummary: activeReplySummary,
            removedQrIds: removedKeys,
            excludedFromReplyQrIds: excludedKeys,
          }
        : null,
    [
      hasRepWork,
      batchEmailId,
      suggestions,
      allShipments,
      editedFieldsKey,
      ltlCustomer,
      ltlCustomerStaff,
      persistedRuns,
      quoteSelectionOverrides,
      expandedKeys,
      repliedKeys,
      acceptedQrIds,
      editedDraft,
      activeReplySummary,
      removedKeys,
      excludedKeys,
    ]
  );
  const { clear: clearSavedBatch } = usePersistedBatchLTLState({
    storageKey,
    threadId,
    stateToSave,
    urgentKey: getUrgentSaveKey({
      runs: persistedRuns,
      repliedKeys,
      acceptedQrIds,
      replySummary: activeReplySummary,
      shipments: allShipments,
      removedKeys,
      excludedKeys,
    }),
  });

  // Sidebar state resets when the rep leaves the thread; put the saved reply summary back
  // so Kit describes the same reply this panel shows.
  useEffect(() => {
    if (!savedReplySummary) {
      return;
    }

    setCurrentState((prevState) =>
      prevState.batchLTLReplySummary
        ? prevState
        : { ...prevState, batchLTLReplySummary: savedReplySummary }
    );
  }, [savedReplySummary, setCurrentState]);

  const handleStartOver = () => {
    clearSavedBatch();
    setCurrentState((prevState) =>
      prevState.batchLTLReplySummary?.emailId === batchEmailId
        ? { ...prevState, batchLTLReplySummary: null }
        : prevState
    );
    onStartOver?.();
  };

  const rateRows = (shipmentKeys: number[]) => {
    if (isRating) {
      toast({
        description:
          'Wait for the current batch rating to finish before retrying.',
        variant: 'destructive',
      });
      return;
    }

    if (!ltlCustomer?.customerId || !ltlCustomerStaff?.ownerId) {
      toast({
        description:
          'Select a customer and customer staff before requesting rates.',
        variant: 'destructive',
      });
      return;
    }

    setQuoteSelectionOverrides((prev) => omitKeys(prev, shipmentKeys));
    setRepliedKeys((prev) => prev.filter((key) => !shipmentKeys.includes(key)));
    const requests = shipments
      .filter(({ key }) => shipmentKeys.includes(key))
      .map((shipment) => {
        const values =
          formRegistryRef.current.get(shipment.key)?.getValues() ?? shipment;

        return {
          key: shipment.key,
          formValues: {
            ...values,
            stops: normalizeStopsFromLocationLine(values.stops),
          },
        };
      });

    void rateShipments(requests);
  };

  const handleApplyBulkEdit = (values: BatchLTLBulkEditValues) => {
    shipments
      .filter(({ key }) => selectedKeys.includes(key))
      .forEach((shipment) => {
        const formMethods = formRegistryRef.current.get(shipment.key);
        if (!formMethods) {
          return;
        }

        const edited = applyBatchLTLBulkEdit(
          withShipmentMeta(formMethods.getValues(), shipment),
          values,
          ltlAccessorials
        );
        const options = { shouldDirty: true };
        formMethods.setValue('stops', edited.stops, options);
        formMethods.setValue('pickupDate', edited.pickupDate, options);
        formMethods.setValue(
          'pickupCloseDate',
          edited.pickupCloseDate,
          options
        );
        formMethods.setValue('ltl', edited.ltl, options);
      });
    setIsBulkEditOpen(false);
    setSelectedKeys([]);
  };

  const handleToggleQuote = (shipmentKey: number, quote: LTLQuote) => {
    const quoteId = getLTLQuoteIdentity(quote);
    const current =
      rows.find((row) => row.shipment.key === shipmentKey)?.selectedQuoteIds ??
      [];
    setQuoteSelectionOverrides((prev) => ({
      ...prev,
      [shipmentKey]: current.includes(quoteId)
        ? current.filter((id) => id !== quoteId)
        : [...current, quoteId],
    }));
  };

  const handleAddShipment = () => {
    const shipmentKey = -Date.now();
    setAllShipments((prev) => [
      ...prev,
      buildEmptyBatchLTLShipment({
        key: shipmentKey,
        label: `Shipment ${shipments.length + 1}`,
        template: shipments[0],
        isOriginShared: Boolean(sharedOriginLabel),
        pickupHours: quickQuoteConfig,
      }),
    ]);
    setExpandedKeys((prev) => [...prev, shipmentKey]);
  };

  // Takes the quote request off Kit's list, the way Kit's own dismiss does.
  const dropFromKit = (qrId: number) =>
    setCurrentState((prevState) => ({
      ...prevState,
      clickedSuggestion:
        prevState.clickedSuggestion?.id === qrId
          ? null
          : prevState.clickedSuggestion,
      batchSelectedSuggestionIds: (
        prevState.batchSelectedSuggestionIds ?? []
      ).filter((id) => id !== qrId),
      curSuggestionList: prevState.curSuggestionList.filter(
        ({ id }) => id !== qrId
      ),
    }));

  const handleUndoRemove = (shipmentKey: number) => {
    const pending = pendingRejectsRef.current.get(shipmentKey);
    if (pending) {
      clearTimeout(pending.timer);
      pendingRejectsRef.current.delete(shipmentKey);
    }
    setRemovedKeys((prev) => prev.filter((key) => key !== shipmentKey));
  };

  // Removing takes the shipment off the panel, the reply and the Accepted update. Its
  // quote request (the email's, or the one rating created) is rejected after Undo runs out.
  const handleRemove = (shipment: BatchLTLShipmentInput) => {
    const { key: shipmentKey, quoteRequestId, label } = shipment;
    const row = rows.find(
      ({ shipment: rowShipment }) => rowShipment.key === shipmentKey
    );
    if (row?.status === BatchLTLShipmentStatus.Rating) {
      toast({
        description: `${label} is being rated. Remove it after rating finishes.`,
        variant: 'destructive',
      });
      return;
    }
    if (row?.status === BatchLTLShipmentStatus.InReply) {
      toast({
        description: `${label} has already been accepted and cannot be removed.`,
        variant: 'destructive',
      });
      return;
    }
    setRemovedKeys((prev) => [...prev, shipmentKey]);
    setSelectedKeys((prev) => prev.filter((key) => key !== shipmentKey));

    if (quoteRequestId > 0) {
      const timer = setTimeout(() => {
        pendingRejectsRef.current.delete(shipmentKey);
        void rejectQuoteRequest(quoteRequestId).then((isRejected) => {
          if (isRejected) {
            dropFromKit(quoteRequestId);
          }
        });
      }, BATCH_LTL_REMOVE_UNDO_MS);
      pendingRejectsRef.current.set(shipmentKey, {
        timer,
        qrId: quoteRequestId,
      });
    }

    toast({
      description: `Removed ${label}.`,
      duration: BATCH_LTL_REMOVE_UNDO_MS,
      action: (
        <ToastAction
          altText={`Undo removing ${label}`}
          onClick={() => handleUndoRemove(shipmentKey)}
        >
          Undo
        </ToastAction>
      ),
    });
  };

  const handleTerminatingAction = async (
    terminatingAction: QuoteTerminatingAction,
    isSend = false
  ) => {
    try {
      const { succeeded, failed } = await submitBatchLTLFinalQuotes({
        finalQuotes: draftShipments.map(({ shipment, selectedQuotes }) => ({
          key: shipment.key,
          quoteRequestId: shipment.quoteRequestId,
          selectedQuotes,
        })),
        customerExternalTMSId: ltlCustomer?.customerId ?? null,
        terminatingAction,
      });
      const completedKeys = succeeded.map(({ key }) => key);
      const completedQrIds = succeeded.map(
        ({ quoteRequestId }) => quoteRequestId
      );
      const remainingCount = rows.filter(
        ({ shipment, status, selectedQuoteIds, isIncludedInReply }) =>
          isIncludedInReply &&
          !completedKeys.includes(shipment.key) &&
          status !== BatchLTLShipmentStatus.InReply &&
          !(
            status === BatchLTLShipmentStatus.Rated &&
            selectedQuoteIds.length === 0
          )
      ).length;

      // Kit reads the summary so its bubble and rows match this panel.
      const replySummary: BatchLTLReplySummary = {
        emailId: batchEmailId,
        action: getReplyAction(terminatingAction, isSend),
        inReplyCount: completedKeys.length,
        remainingCount,
        hasSharedOrigin: Boolean(sharedOriginLabel),
      };
      const commitCompletion = () => {
        setRepliedKeys((previous) => [
          ...new Set([...previous, ...completedKeys]),
        ]);
        setCurrentState((prevState) => ({
          ...prevState,
          ...(completedQrIds.length > 0
            ? applyCompleteKitSuggestions(
                prevState,
                completedQrIds,
                SuggestionPipelines.QuickQuote
              )
            : {}),
          batchLTLReplySummary: replySummary,
        }));
      };
      if (completedQrIds.length > 0) {
        setAcceptedQrIds((previous) => [
          ...new Set([...previous, ...completedQrIds]),
        ]);
      }
      if (failed.length > 0) {
        toast({
          description: `${failed.length} shipment${failed.length === 1 ? '' : 's'} could not be accepted. Retry them from the panel.`,
          variant: 'destructive',
        });
      }
      if (terminatingAction !== QuoteTerminatingAction.Reply) {
        commitCompletion();
      }
      return {
        accepted: failed.length === 0,
        commitCompletion,
      };
    } catch (error) {
      captureException(error, {
        functionName: 'BatchLTLQuoteForm.handleTerminatingAction',
      });
      return { accepted: false, commitCompletion: () => undefined };
    }
  };

  const handleReply = async (draftBody: string, isSendRequested: boolean) => {
    if (!REPLY_DRAFT_PLATFORMS.includes(drumkitPlatform) || !draftBody.trim()) {
      return;
    }

    // Front only supports drafts; the rep sends from Front.
    const isSend = isSendRequested && drumkitPlatform !== DrumkitPlatform.Front;

    setIsSendingReply(true);
    try {
      const replyPayload = {
        threadItemId,
        draftBody: draftBody.trim(),
        replyMode: DraftReplyMode.ReplyAll,
        to: recipients.to,
        cc: recipients.cc,
        bcc: recipients.bcc,
      };
      const { accepted, commitCompletion } = await handleTerminatingAction(
        QuoteTerminatingAction.Reply,
        isSend
      );
      if (!accepted) {
        return;
      }

      const mailClient = createMailClientInstance(drumkitPlatform);
      if (isSend) {
        await mailClient.sendReply(replyPayload);
      } else {
        await mailClient.draftReply(replyPayload);
      }
      commitCompletion();

      toast({
        description: isSend
          ? 'Successfully sent quote reply.'
          : 'Successfully created draft reply.',
        variant: 'success',
      });
    } catch (error) {
      captureException(error, {
        functionName: 'BatchLTLQuoteForm.handleReply',
      });
      toast({
        description: isSend
          ? 'Unable to send quote reply.'
          : 'Unable to create draft reply.',
        variant: 'destructive',
      });
    } finally {
      setIsSendingReply(false);
    }
  };

  let selectAllState: boolean | 'indeterminate' = false;
  if (shipments.length > 0 && selectedKeys.length === shipments.length) {
    selectAllState = true;
  } else if (selectedKeys.length > 0) {
    selectAllState = 'indeterminate';
  }
  const selectedAccessorials = uniqueByCode(
    shipments
      .filter((shipment) => selectedKeys.includes(shipment.key))
      .flatMap((shipment) => shipment.ltl?.selectedAccessorials ?? [])
  );

  return (
    <FormProvider {...panelFormMethods}>
      <Flex
        direction='col'
        gap='md'
        className='w-full'
        data-testid='batch-ltl-form'
      >
        <Flex direction='col' className='w-full'>
          <Flex justify='between' align='center' className='w-full'>
            <Typography variant='body-xs' textColor='muted'>
              {pluralize(shipments.length, 'shipment')} in this batch
            </Typography>
            {hasRepWork && (
              <button
                type='button'
                className='text-xs text-info-600 underline-offset-2 hover:underline'
                onClick={handleStartOver}
              >
                Start over
              </button>
            )}
          </Flex>
          {sharedOriginLabel && (
            <Typography variant='body-xs' textColor='lessMuted'>
              All ship from{' '}
              <Typography as='span' variant='body-xs' weight='medium'>
                {sharedOriginLabel}
              </Typography>
            </Typography>
          )}
        </Flex>

        <BatchLTLCustomerSection
          suggestedCustomerId={
            saved?.ltlCustomer?.customerId ??
            suggestions[0]?.suggested?.customerExternalTMSID
          }
          preferredStaffOwnerId={saved?.ltlCustomerStaff?.ownerId}
        />

        {hasRuns && (
          <BatchLTLSummaryBar
            counts={statusCounts}
            onJumpToReply={() =>
              draftSectionRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
              })
            }
          />
        )}

        {activeReplySummary && (
          <Flex
            align='center'
            gap='xs'
            className='w-full rounded border border-success-300 bg-success-50 px-2.5 py-1.5'
            data-testid='batch-ltl-reply-confirmation'
          >
            <CheckCircle2Icon className='h-3.5 w-3.5 shrink-0 text-success-600' />
            <Typography variant='body-xs' className='text-success-700'>
              {getReplyConfirmationText(
                activeReplySummary.action,
                activeReplySummary.inReplyCount,
                activeReplySummary.remainingCount
              )}
            </Typography>
          </Flex>
        )}

        <Flex justify='between' align='center' className='w-full'>
          <Flex align='center' gap='sm'>
            <Checkbox
              label='Select all'
              labelClassName='text-xs text-neutral-700'
              checked={selectAllState}
              onCheckedChange={(checked) =>
                setSelectedKeys(
                  checked === true ? shipments.map(({ key }) => key) : []
                )
              }
              aria-label='Select all shipments'
            />
            {selectedKeys.length > 0 && (
              <Typography variant='body-xxs' textColor='muted' as='span'>
                {selectedKeys.length} selected
              </Typography>
            )}
          </Flex>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            className='h-7 px-2 text-xs text-info-600 hover:bg-info-50'
            disabled={selectedKeys.length === 0}
            onClick={() => setIsBulkEditOpen(true)}
            buttonNamePosthog={ButtonNamePosthog.BatchLTLQuoteApplyBulkEdit}
          >
            <PencilIcon className='mr-1 h-3 w-3' />
            Edit selected
          </Button>
        </Flex>

        {isBulkEditOpen && selectedKeys.length > 0 && (
          <BatchLTLBulkEdit
            selectedCount={selectedKeys.length}
            accessorials={ltlAccessorials}
            selectedAccessorials={selectedAccessorials}
            onApply={handleApplyBulkEdit}
            onCancel={() => setIsBulkEditOpen(false)}
          />
        )}

        {rows.map(
          ({
            shipment,
            index,
            validation,
            run,
            status,
            selectedQuoteIds,
            isIncludedInReply,
          }) => {
            const { key: shipmentKey } = shipment;
            const isFromEmail = !isAddedShipmentKey(shipmentKey);

            return (
              <BatchLTLShipmentForm
                key={shipmentKey}
                shipment={shipment}
                onRegister={handleRegisterForm}
                onValuesChange={handleValuesChange}
              >
                <BatchLTLShipmentCard
                  shipmentIndex={index}
                  shipment={shipment}
                  status={status}
                  validation={validation}
                  run={run}
                  selectedQuoteIds={selectedQuoteIds}
                  accessorials={ltlAccessorials}
                  suggestedFields={
                    suggestions.find(({ id }) => id === shipmentKey)
                      ?.suggested ?? null
                  }
                  sharedOriginLabel={sharedOriginLabel}
                  isSelected={selectedKeys.includes(shipmentKey)}
                  isExpanded={expandedKeys.includes(shipmentKey)}
                  isIncludedInReply={isIncludedInReply}
                  isRateDisabled={status === BatchLTLShipmentStatus.Rating}
                  onSelectedChange={(isSelected) =>
                    setSelectedKeys((prev) =>
                      toggleKey(prev, shipmentKey, isSelected)
                    )
                  }
                  onExpandedChange={(isExpanded) =>
                    setExpandedKeys((prev) =>
                      toggleKey(prev, shipmentKey, isExpanded)
                    )
                  }
                  onIncludedInReplyChange={(isIncluded) =>
                    setExcludedKeys((prev) =>
                      toggleKey(prev, shipmentKey, !isIncluded)
                    )
                  }
                  onToggleQuote={(quote) =>
                    handleToggleQuote(shipmentKey, quote)
                  }
                  onRate={() => rateRows([shipmentKey])}
                  onRemove={() => handleRemove(shipment)}
                  onOpenInQuickQuote={
                    onOpenInQuickQuote && isFromEmail
                      ? () => onOpenInQuickQuote(shipment.quoteRequestId)
                      : undefined
                  }
                />
              </BatchLTLShipmentForm>
            );
          }
        )}

        <Button
          type='button'
          variant='outline'
          size='sm'
          className='h-8 w-full text-xs'
          onClick={handleAddShipment}
          buttonNamePosthog={ButtonNamePosthog.BatchLTLQuoteAddShipment}
        >
          <PlusIcon className='mr-1 h-3 w-3' />
          Add shipment
        </Button>

        <Button
          type='button'
          className='w-full'
          disabled={readyRows.length === 0 || isRating}
          onClick={() =>
            rateRows(readyRows.map(({ shipment }) => shipment.key))
          }
          buttonNamePosthog={ButtonNamePosthog.GetBatchLTLQuote}
          logProperties={{
            readyCount: readyRows.length,
            shipmentCount: shipments.length,
          }}
        >
          {isRating ? (
            <ButtonLoader />
          ) : (
            `Get batch quote (${readyRows.length} of ${shipments.length} ready)`
          )}
        </Button>
        {!hasRuns && skippedCount > 0 && (
          <Typography variant='body-xxs' textColor='muted' align='center'>
            {skippedCount} with missing info {skippedCount === 1 ? 'is' : 'are'}{' '}
            skipped until fixed.
          </Typography>
        )}

        {hasRuns && supportsDraftResponse(drumkitPlatform) && (
          <div ref={draftSectionRef} className='w-full scroll-mt-2'>
            <BatchLTLDraftSection
              draft={draft}
              isRepEdited={isRepEdited}
              drumkitPlatform={drumkitPlatform}
              recipients={recipients}
              isSharedMailbox={
                Boolean(isSharedMailboxUser) || Boolean(isSharedMailboxEmail)
              }
              isOutlookReply={isOutlookReply}
              isSendingReply={isSendingReply}
              onRepEdit={saveRepEdit}
              onResetToGenerated={resetToGenerated}
              onCopied={() =>
                void handleTerminatingAction(QuoteTerminatingAction.Copy)
              }
              onReply={(draftBody, isSend) =>
                void handleReply(draftBody, isSend)
              }
            />
          </div>
        )}
      </Flex>
    </FormProvider>
  );
}

function omitKeys<T>(
  record: Record<number, T>,
  keys: number[]
): Record<number, T> {
  return Object.fromEntries(
    Object.entries(record).filter(([key]) => !keys.includes(Number(key)))
  );
}

function uniqueByCode(
  accessorials: LTLSelectedAccessorial[]
): LTLSelectedAccessorial[] {
  return [
    ...new Map(
      accessorials.map((accessorial) => [accessorial.code, accessorial])
    ).values(),
  ];
}

function pickByKey<T>(
  record: Record<number, T>,
  keys: number[]
): Record<number, T> {
  return Object.fromEntries(
    Object.entries(record).filter(([key]) => keys.includes(Number(key)))
  );
}

function toggleKey(keys: number[], key: number, isOn: boolean): number[] {
  const others = keys.filter((candidate) => candidate !== key);

  return isOn ? [...others, key] : others;
}

// A card's form values with the shipment's batch fields (key, quote request, label, notes).
function withShipmentMeta(
  values: QuickQuoteInputs,
  shipment: Undef<BatchLTLShipmentInput>
): BatchLTLShipmentInput {
  return {
    ...values,
    key: shipment?.key ?? 0,
    quoteRequestId: shipment?.quoteRequestId ?? 0,
    label: shipment?.label ?? '',
    notes: shipment?.notes ?? '',
  };
}

// Kit's own reject for a quote request; the shipment is already off the panel either way.
async function rejectQuoteRequest(qrId: number): Promise<boolean> {
  const result = await updateQuoteRequestSuggestion(
    qrId,
    SuggestionStatus.Rejected
  );
  if (result.isErr()) {
    captureException(result.error, {
      functionName: 'BatchLTLQuoteForm.rejectQuoteRequest',
    });
  }

  return result.isOk();
}

// The rep's carrier picks apply only while every picked quote is still in the server's
// result; otherwise the default top picks show.
function getSelectedQuoteIds(
  run: Undef<BatchLTLShipmentRun>,
  override: Undef<string[]>
): string[] {
  const defaults = run?.defaultSelectedQuoteIds ?? [];
  if (!override) {
    return defaults;
  }

  const quoteIds = (run?.quotes ?? []).map(getLTLQuoteIdentity);

  return override.every((id) => quoteIds.includes(id)) ? override : defaults;
}

type UrgentSaveParams = {
  runs: Record<number, BatchLTLPersistedRun>;
  repliedKeys: number[];
  acceptedQrIds: number[];
  replySummary: Maybe<BatchLTLReplySummary>;
  shipments: BatchLTLShipmentInput[];
  removedKeys: number[];
  excludedKeys: number[];
};

// Job ids, replies, added or removed shipments, quote requests rating created and reply
// exclusions are saved without waiting.
function getUrgentSaveKey({
  runs,
  repliedKeys,
  acceptedQrIds,
  replySummary,
  shipments,
  removedKeys,
  excludedKeys,
}: UrgentSaveParams): string {
  return JSON.stringify([
    Object.entries(runs).map(([key, run]) => [key, run.jobId, run.status]),
    repliedKeys,
    acceptedQrIds,
    replySummary,
    shipments.map(({ key, quoteRequestId }) => [key, quoteRequestId]),
    removedKeys,
    excludedKeys,
  ]);
}

function getReplyAction(
  terminatingAction: QuoteTerminatingAction,
  isSend: boolean
): BatchLTLReplyAction {
  if (terminatingAction === QuoteTerminatingAction.Copy) {
    return BatchLTLReplyAction.Copied;
  }

  return isSend ? BatchLTLReplyAction.Sent : BatchLTLReplyAction.Drafted;
}

function getShipmentStatus(
  isValid: boolean,
  run: Undef<BatchLTLShipmentRun>,
  isInReply: boolean
): BatchLTLShipmentStatus {
  if (run?.status === BatchLTLShipmentStatus.Rated && isInReply) {
    return BatchLTLShipmentStatus.InReply;
  }

  if (run) {
    return run.status;
  }

  return isValid
    ? BatchLTLShipmentStatus.Ready
    : BatchLTLShipmentStatus.Invalid;
}
